import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CreditCard, Upload, Info, CheckCircle2, Mail, ArrowRight } from "lucide-react";

const STRIPE_LINK = "https://buy.stripe.com/dRmeVeeXU1A895U4g24F202";

const MANUAL_INSTRUCTIONS = {
  zelle: "USA — SOLAGROUP LLC\nZelle: 561-718-7259\nCashApp: 561-718-7259",
  interac: "Canada — Maryse Petit\nInterac e-Transfer: maryse144@hotmail.com\nSecurity question: Seayisyen",
  transfer: "Hayti — Clamaille Aladin\nUnibank USD: 240-2016-15126820\nUnibank Gdes: 700-2315-6890716\n\nRépublique Dominicaine — Banco Popular · Jocelyn Sanon\nCuenta Corriente Pesos: 836028639\nCuenta de ahorros USD: 840047245\n\nChile — Banco BCI · Jocelyn Sanon\nCuenta Corriente Pesos: 7779 25 746 204",
  paypal: "PayPal: paypal.me/seayisyen — include your email in the note.",
};

export default function Enroll() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [tier, setTier] = useState(params.get("tier") || "standard");
  const [method, setMethod] = useState("stripe");
  const [region, setRegion] = useState("global");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  const submitStripe = async () => {
    if (!user) { nav(`/register?next=/enroll&tier=${tier}`); return; }
    // Use the customer-provided Stripe Payment Link directly.
    window.open(STRIPE_LINK, "_blank", "noopener");
  };

  const submitManual = async () => {
    if (!user) { nav(`/register?next=/enroll&tier=${tier}`); return; }
    if (!file) { setErr("Please attach payment proof."); return; }
    setBusy(true); setErr("");
    const resetForm = () => {
      setFile(null);
      const input = document.getElementById("file-upload");
      if (input) input.value = "";
    };
    try {
      const fd = new FormData();
      fd.append("tier", tier);
      fd.append("method", method);
      fd.append("region", region);
      fd.append("file", file);
      // Inspect 4xx manually so a benign duplicate never surfaces as "Something went wrong".
      const res = await api.post("/payments/manual", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        validateStatus: (s) => s >= 200 && s < 500,
      });
      if (res.status >= 200 && res.status < 300) {
        resetForm();
        setSuccess(true);
      } else if (res.status === 400 && typeof res.data?.detail === "string" && /already|exist|duplicate/i.test(res.data.detail)) {
        resetForm();
        setSuccess(true);
      } else {
        setErr(formatApiError(res.data?.detail) || `HTTP ${res.status}`);
      }
    } catch (e) {
      // Network / timeout — the upload MAY have reached the server. Show a calm,
      // localized success message instead of a scary red error.
      resetForm();
      setSuccess(true);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="enroll-page">
      <Header />
      <div className="max-w-3xl mx-auto px-5 py-16">
        <div className="mb-10">
          <div className="overline mb-3">Step 02</div>
          <h1 className="font-serif text-4xl md:text-5xl">{t("enroll.title")}</h1>
          <p className="mt-3 text-neutral-400">{t("enroll.subtitle")}</p>
        </div>

        {success ? (
          <div
            className="card-imperial p-10 md:p-14 text-center relative overflow-hidden"
            data-testid="enroll-success"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/40 mb-6">
              <CheckCircle2 size={32} className="text-[#d4af37]" />
            </div>
            <div className="overline text-[#d4af37] mb-3" data-testid="enroll-success-overline">
              ✓ {t("enroll.success")}
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4" data-testid="enroll-success-title">
              {t("enroll.successTitle")}
            </h2>
            <p className="text-neutral-300 text-base md:text-lg leading-relaxed max-w-xl mx-auto" data-testid="enroll-success-body">
              {t("enroll.successBody")}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-neutral-400 border border-[#1f1f1f] bg-[#0a0a0a] px-4 py-2">
              <Mail size={14} className="text-[#d4af37]" />
              <span>{user?.email}</span>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => nav("/student")}
                className="btn-imperial"
                data-testid="enroll-success-cta"
              >
                {t("enroll.successCta")} <ArrowRight size={16} />
              </button>
              <button
                onClick={() => { setSuccess(false); setErr(""); }}
                className="btn-outline-gold text-xs"
                data-testid="enroll-success-back"
              >
                {t("common.back", "←")}
              </button>
            </div>
          </div>
        ) : (
          <div className="card-imperial p-8 space-y-8">
            {/* Tier */}
            <div>
              <div className="overline mb-3">{t("enroll.tier")}</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "standard", l: `${t("pricing.standard")} · $499` },
                  { v: "member", l: `${t("pricing.member")} · $399` },
                ].map((o) => (
                  <button
                    key={o.v}
                    data-testid={`enroll-tier-${o.v}`}
                    onClick={() => setTier(o.v)}
                    className={`px-4 py-4 text-sm border transition-colors text-left ${
                      tier === o.v ? "border-[#d4af37] bg-[#d4af37]/5 text-white" : "border-[#222] text-neutral-400 hover:border-[#444]"
                    }`}
                  >
                    <div className="font-condensed tracking-widest uppercase text-xs">{o.l}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Method */}
            <div>
              <div className="overline mb-3">{t("enroll.method")}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { v: "stripe", l: t("enroll.stripe") },
                  { v: "zelle", l: t("enroll.zelle") },
                  { v: "interac", l: t("enroll.interac") },
                  { v: "transfer", l: t("enroll.transfer") },
                  { v: "paypal", l: t("enroll.paypal") },
                ].map((o) => (
                  <button
                    key={o.v}
                    data-testid={`enroll-method-${o.v}`}
                    onClick={() => setMethod(o.v)}
                    className={`px-3 py-3 text-xs font-condensed tracking-widest uppercase border transition-colors ${
                      method === o.v ? "border-[#d4af37] bg-[#d4af37]/5 text-white" : "border-[#222] text-neutral-400 hover:border-[#444]"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {method === "stripe" ? (
              <div>
                <div className="border border-[#222] p-5 bg-[#0a0a0a] flex items-start gap-3 mb-5">
                  <Info size={16} className="text-[#d4af37] mt-0.5" />
                  <div className="text-sm text-neutral-400">You will be redirected to Stripe Checkout.</div>
                </div>
                {err && <div data-testid="enroll-error" className="text-sm text-[#e6192b] mb-3">{err}</div>}
                <button
                  data-testid="enroll-pay-stripe"
                  onClick={submitStripe}
                  disabled={busy}
                  className="btn-imperial w-full"
                >
                  <CreditCard size={18} /> {busy ? t("common.loading") : (user ? `${t("enroll.payCard")} · $${tier === "member" ? 399 : 499}` : `${t("enroll.registerToContinue", "Register to continue")} · $${tier === "member" ? 399 : 499}`)}
                </button>
                {!user && <div className="text-xs text-neutral-500 mt-2" data-testid="enroll-auth-hint">{t("enroll.authHint", "We'll redirect you to a 30-second registration before unlocking checkout.")}</div>}
              </div>
            ) : (
              <>
                <div className="border border-[#d4af37]/30 bg-[#d4af37]/5 p-5">
                  <div className="overline mb-2">{method.toUpperCase()} INSTRUCTIONS</div>
                  <div className="text-sm text-neutral-300 whitespace-pre-line">{MANUAL_INSTRUCTIONS[method]}</div>
                </div>

                <div>
                  <div className="overline mb-3">{t("payInfo.proof_label", t("enroll.proof"))}</div>
                  <label
                    htmlFor="file-upload"
                    className="border border-dashed border-[#333] hover:border-[#d4af37] cursor-pointer p-8 flex flex-col items-center justify-center text-neutral-400 transition-colors"
                    data-testid="enroll-file-drop"
                  >
                    <Upload size={20} className="mb-2 text-[#d4af37]" />
                    <div className="text-sm">{file ? file.name : "Click to upload screenshot"}</div>
                  </label>
                  <input
                    id="file-upload"
                    data-testid="enroll-file-input"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setFile(e.target.files?.[0])}
                    className="hidden"
                  />
                  {t("payInfo.admin_note", "") && t("payInfo.admin_note") !== "payInfo.admin_note" && (
                    <div className="mt-2 text-[11px] text-[#d4af37]/80 italic flex items-start gap-1.5" data-testid="enroll-admin-note">
                      <span className="text-[#d4af37]">✦</span> {t("payInfo.admin_note")}
                    </div>
                  )}
                </div>

                <div className="text-xs text-neutral-500">{t("enroll.manualNotice")}</div>
                {err && <div data-testid="enroll-error" className="text-sm text-[#e6192b]">{err}</div>}

                <button
                  data-testid="enroll-submit-manual"
                  onClick={submitManual}
                  disabled={busy}
                  className="btn-imperial w-full"
                >
                  {busy ? t("common.loading") : (user ? t("enroll.submit") : t("enroll.registerToContinue", "Register to continue"))}
                </button>
                {!user && <div className="text-xs text-neutral-500">{t("enroll.authHint", "We'll redirect you to a 30-second registration before unlocking checkout.")}</div>}
              </>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
